/* eslint-disable react-hooks/rules-of-hooks */
import { memo, useEffect, useRef, useState } from "react";
import RiskBlockedModal from "@zenith/components/ui/RiskBlockedModal/risk-blocked-modal";
import { isRiskBlocked } from "@zenith/utils/riskChallenge";
import { Navigate, useNavigate } from "react-router-dom";

import Button from "@mui/material/Button";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

import {
  ButtonComponent,
  ModalComponent,
  SpinnerModalComponent,
} from "@zenith/components";
import {
  LOCKED_PASSCODE_ERROR_CODE,
  DEFAULT_CHANNEL,
  DEFAULT_REFUND_TYPE,
  GENERIC_ERROR_CODE,
  LIMIT_UNAUTHORIZED_AMOUNT_MODAL_BODY,
  REFUND_TIMEOUT_ERROR_MODAL_BODY,
  SUCCESS_API_CODE,
  SWITCH_UNAVAILABLE_ERROR_CODE,
  TIMEOUT_ERROR_CODE,
  TIMEOUT_ERROR_MODAL_TITLE,
  TRANSACTIONS_PIN_LENGTH,
  UNAUTHORIZED_AMOUNT_ERROR_MODAL_TITLE,
  UNAUTHORIZED_BALANCE_LIMIT_ERROR_CODE,
  WRONG_PASSCODE_ERROR_CODE,
  GENERIC_ERROR_MESSAGE,
  TRANSACTION_PIN_PAGE_LABEL,
  TRANSACTION_PIN_PAGE_SUBTITLE,
  TRANSACTION_PIN_PAGE_TITLE,
  SWITCH_UNAVAILABLE_MESSAGE,
  WRONG_PIN_FIRST_TRY,
  WRONG_PIN_SECOND_TRY,
  TRANSACTION_PIN_LOCKED_ERROR_MESSAGE,
  TRANSACTION_PIN_MAXIMUM_ATTEMPTS_ERROR_MESSAGE,
  PIN_CODE_REQUIRED_MESSAGE,
  DISCARD_REFUND_BUTTON,
  TRY_AGAIN_BUTTON,
  PIN_CODE_REQUIRED_FOUR_DIGITS_MESSAGE,
  DEFAULT_CURRENCY,
  DEFAULT_COUNTRY_CODE,
} from "@zenith/data/constants";
import { encrypt, getApiErrorMessage } from "@zenith/utils";
import {
  ApiResponseDTOAuthenticationDTO,
  ApiResponseDTOEncryptionDTO,
  ApiResponseDTOTransactionPinDetailDTO,
  AuthenticationApiService,
  UtilityApiService,
  WalletApiService,
} from "@zenith/services/user";
import { FormFieldErrorType } from "@zenith/types";
import { useTransactionsContext } from "@zenith/context";
import {
  ApiResponseDTOOperationDTO,
  OperationDTO,
  OperationsApiService,
} from "@zenith/services/transactions";
import {
  DASHBOARD,
  TRANSACTIONS_REFUND_AUTHENTICATION,
  TRANSACTIONS_REFUND_OVERVIEW,
  TRANSACTIONS_REFUND_SUCCESS,
} from "@zenith/routes/paths";
import { PaymentsPinPageComponent } from "@zenith/features/Payments/components";
import { usePollTransactionStatus } from "@zenith/hooks/usePollTransactionStatus";
import ChevronLeft from "@zenith/assets/icons/ChevronLeft";

const NUMBER_OF_ATTEMPTS = 2;

/* eslint-disable-next-line */
export interface RefundTransactionPinPageProps {}

const RefundTransactionPinPage = memo(function RefundTransactionPinPage(
  props: RefundTransactionPinPageProps
) {
  const xApiKey = import.meta.env.VITE_X_API_KEY;
  const navigate = useNavigate();

  const {
    transactionsContext,
    setTransactionsContext,
    clearTransactionsContext,
  } = useTransactionsContext();
  const {
    amount,
    bankName,
    beneficiaryName,
    feeValue,
    refundReason,
    nameEnquiryRef,
    bankCode,
    refundedOperationId: operationId,
  } = transactionsContext;

  const initialFormErrorState = {
    pin: {
      message: "",
      hasError: false,
    },
  };

  const [formData, setFormData] = useState<{ pin: string }>({ pin: "" }),
    [formErrors, setFormErrors] = useState<{ pin: FormFieldErrorType }>(
      initialFormErrorState
    ),
    [newPin, setNewPin] = useState<Array<number | string>>(
      new Array(TRANSACTIONS_PIN_LENGTH).fill("")
    );

  const [showAccountLockedModal, setShowAccountLockedModal] =
    useState<boolean>(false);
  const [showBalanceOrLimitError, setShowBalanceOrLimitError] =
    useState<boolean>(false);
  const [showTransactionTimeoutError, setShowTransactionTimeoutError] =
    useState<boolean>(false);
  const [is2FAEnabled, setIs2FAEnabled] = useState<boolean>(false);
  const [showRiskBlocked, setShowRiskBlocked] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const transactionIdRef = useRef<string>("");

  // Create a ref to store the latest transactionsContext
  const transactionContextRef = useRef(transactionsContext);

  useEffect(() => {
    transactionContextRef.current = transactionsContext;
  }, [transactionsContext]);

  const BalanceLimitErrorModal = ModalComponent;
  const TransactionTimeoutErrorModal = ModalComponent;

  useEffect(() => {
    AuthenticationApiService.getVerification2Fa()
      .then((response: ApiResponseDTOAuthenticationDTO) => {
        if (
          "data" in response &&
          response.data &&
          response.internalCode === SUCCESS_API_CODE
        ) {
          setIs2FAEnabled(response.data.authentication2fa as boolean);
        }
      })
      .catch((error) => new Error(error));
  }, []);

  if (!amount) {
    return <Navigate to={TRANSACTIONS_REFUND_OVERVIEW} replace />;
  }

  function clearContext(): void {
    clearTransactionsContext();
  }

  function handleBackButton(): void {
    navigate(TRANSACTIONS_REFUND_OVERVIEW);
  }
  const getValidStatus = (status?: string): OperationDTO.status => {
    if (
      status &&
      Object.values(OperationDTO.status).includes(status as OperationDTO.status)
    ) {
      return status as OperationDTO.status;
    }
    return OperationDTO.status.SUCCESS;
  };

  const {
    startPolling,
    isLoading: isPolling,
    stopPolling,
    //eslint-disable-next-line
  } = usePollTransactionStatus(
    (response) => {
      setTransactionsContext({
        ...transactionContextRef.current,
        status: getValidStatus(response?.data?.status),
        transactionDate: response?.data?.date || new Date().toISOString(),
        transactionId:
          response?.data?.transactionId || transactionIdRef.current,
      });
      navigate(TRANSACTIONS_REFUND_SUCCESS, {
        replace: true,
        state: { fromPolling: true },
      });
    },
    (error) => {
      setTransactionsContext({
        ...transactionContextRef.current,
        status: OperationDTO.status.DECLINED,
        errorMessage: error.message,
      });
      navigate(TRANSACTIONS_REFUND_SUCCESS, { replace: true });
    }
  );

  function handlePinValidationFailure(payload: {
    internalCode?: string;
    message?: string;
    data?: { success?: boolean; availableAttempts?: number };
  }): void {
    const { internalCode, data } = payload;

    switch (internalCode) {
      case WRONG_PASSCODE_ERROR_CODE:
        setFormErrors({
          pin: {
            message:
              data?.availableAttempts === NUMBER_OF_ATTEMPTS
                ? WRONG_PIN_FIRST_TRY
                : WRONG_PIN_SECOND_TRY,
            hasError: !data?.success,
          },
        });
        return;

      case LOCKED_PASSCODE_ERROR_CODE:
        setFormErrors({ pin: { message: "", hasError: !data?.success } });
        setShowAccountLockedModal(true);
        return;

      default:
        // Any other rejection (PIN not set, bad request, 5xx, unexpected body
        // shape): echo the backend's own message - e.g. ZENZ-USER-002's
        // "Transaction PIN has not been set" - falling back to a friendly
        // one only when there isn't a usable message to show.
        setFormErrors({
          pin: {
            message: getApiErrorMessage(payload, GENERIC_ERROR_MESSAGE),
            hasError: true,
          },
        });
    }
  }

  function validateTransactionPin(transactionPin: string): void {
    WalletApiService.validateTransactionPin({
      transactionPin,
    })
      .then((response: ApiResponseDTOTransactionPinDetailDTO) => {
        if ("data" in response && response.internalCode === SUCCESS_API_CODE) {
          if (is2FAEnabled) {
            navigate(TRANSACTIONS_REFUND_AUTHENTICATION, {
              state: {
                mfaToken: response.data?.xmfa ?? "",
              },
            });
          } else {
            postOperationRefund(response.data?.xmfa || "");
            // postTransaction(response.data.xmfa || "");
          }
          return;
        }

        // The API answers 2xx with a business error (e.g. wrong PIN) - the
        // refund never started, so show the failure instead of doing nothing.
        handlePinValidationFailure(response);
      })
      .catch((error) => {
        handlePinValidationFailure(error?.body ?? {});
      });
  }

  function postOperationRefund(xmfa: string): void {
    const payload = {
      beneficiary: {
        account: {
          name: transactionsContext.beneficiaryName,
          number: transactionsContext.beneficiaryAccountNumber,
        },
        bank: {
          code: transactionsContext.bankId || "",
        },
      },
      mfaToken: xmfa,
      merchantPayment: true,
      originalOperationId: transactionsContext.refundedOperationId,
      transaction: {
        amount: transactionsContext.amount || 0,
        countryCode: DEFAULT_COUNTRY_CODE,
        currencyCode: DEFAULT_CURRENCY,
        fees: transactionsContext.feeValue || 0,
        nameEnquiryRef: transactionsContext.nameEnquiryRef || "",
        narration: transactionsContext.refundReason || "REFUND",
        transferType: "NIP",
      },
      transactionId: transactionsContext.transactionId,
    };

    setLoading(true);

    OperationsApiService.postOperationRefund(DEFAULT_CHANNEL, payload)
      .then((response: ApiResponseDTOOperationDTO) => {
        if (response.data?.refund?.transactionId) {
          const updatedContext = {
            ...transactionsContext,

            accountOwner: response.data?.payerAccount?.payerName as string,
            amount: response.data?.refund?.amount as number,
            bankName: response.data?.refund?.bankName as string,
            feeValue: response.data?.refund?.fee,
            sessionId: response.data?.refund?.sessionId,
            status: response.data.status,
            totalAmount: response.data.totalAmount,
            transactionDate: response.data.createdAt,
            operationId: response.data.operationId,
            senderName: response.data?.refund?.senderName,
            refundedTransactionId: response.data?.refund?.originalTransactionId,
            description: response.data.refund?.description,
            paymentMethod: response.data.paymentMethod,
            transactionId: response.data.refund?.transactionId,
          };

          setTransactionsContext(updatedContext);
          transactionIdRef.current = response.data.refund?.transactionId;

          // Start polling with the transaction ID
          startPolling(response.data.refund?.transactionId);
        }
      })
      .catch((error) => {
        stopPolling();
        setLoading(false);
        const { internalCode, dateTime } = error.body;
        if (isRiskBlocked(internalCode)) {
          return setShowRiskBlocked(true);
        }
        let errorMessage = "";
        switch (internalCode) {
          case UNAUTHORIZED_BALANCE_LIMIT_ERROR_CODE:
            return setShowBalanceOrLimitError(true);
          case TIMEOUT_ERROR_CODE:
            return setShowTransactionTimeoutError(true);
          case SWITCH_UNAVAILABLE_ERROR_CODE:
            errorMessage = SWITCH_UNAVAILABLE_MESSAGE;
            break;
          default:
          case GENERIC_ERROR_CODE:
            errorMessage = GENERIC_ERROR_MESSAGE;
            break;
        }
        setTransactionsContext({
          ...transactionsContext,
          transactionDate: dateTime,
          errorMessage,
          status: OperationDTO.status.DECLINED,
        });
        navigate(TRANSACTIONS_REFUND_SUCCESS, {
          replace: true,
        });
      });
  }

  function handleSubmit(): void {
    setLoading(true);
    UtilityApiService.getPublicKey(DEFAULT_CHANNEL, xApiKey)
      .then((response: ApiResponseDTOEncryptionDTO) => {
        if (!response.data) return;
        if (response.success) {
          const publicKeyBase64 = response.data.publicKeyBase64;
          const encryptedPin = encrypt(formData.pin, publicKeyBase64 || "");

          if (publicKeyBase64 && encryptedPin) {
            validateTransactionPin(encryptedPin);
          }
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function onPinChanged(pinEntry: number | string, index: number): void {
    const pin = [...newPin];
    pin[index] = pinEntry;
    setNewPin(pin);
    setFormData({ ...formData, pin: pin.join("") });
  }

  function onPinBlur(): void {
    if (newPin.join("") === "") {
      setFormErrors({
        pin: {
          hasError: true,
          message: PIN_CODE_REQUIRED_MESSAGE,
        },
      });
      return;
    }

    if (
      newPin.join("").length > 0 &&
      newPin.join("").length < TRANSACTIONS_PIN_LENGTH
    ) {
      setFormErrors({
        pin: {
          hasError: true,
          message: PIN_CODE_REQUIRED_FOUR_DIGITS_MESSAGE,
        },
      });
      return;
    }

    setFormErrors({
      pin: {
        hasError: false,
        message: "",
      },
    });
  }

  function isContinueButtonDisabled(): boolean {
    return formData.pin.length < TRANSACTIONS_PIN_LENGTH;
  }

  function handleModalClose(): void {
    clearContext();
    navigate(DASHBOARD, { replace: true });
  }

  function handleBalanceErrorModalCloseBtn() {
    setShowBalanceOrLimitError(false);
  }

  function handleTimeoutErrorModalDismiss() {
    setShowTransactionTimeoutError(false);
  }

  function handleBalanceErrorModalDiscardBtn() {
    clearContext();
    navigate(DASHBOARD, { replace: true });
  }

  function handleTimeoutDiscardRefundModal() {
    clearContext();
    navigate(DASHBOARD, { replace: true });
  }

  function handleRiskBlockedClose() {
    setShowRiskBlocked(false);
    clearContext();
    navigate(DASHBOARD, { replace: true });
  }

  return (
    <>
      <SpinnerModalComponent isLoading={isPolling} />

      <RiskBlockedModal
        open={showRiskBlocked}
        onClose={handleRiskBlockedClose}
      />

      <ModalComponent
        title={TRANSACTION_PIN_LOCKED_ERROR_MESSAGE}
        body={TRANSACTION_PIN_MAXIMUM_ATTEMPTS_ERROR_MESSAGE}
        showModal={showAccountLockedModal}
        onClickClose={handleModalClose}
        onClickButton={handleModalClose}
        buttonText={DISCARD_REFUND_BUTTON}
      />
      <BalanceLimitErrorModal
        title={UNAUTHORIZED_AMOUNT_ERROR_MODAL_TITLE}
        body={LIMIT_UNAUTHORIZED_AMOUNT_MODAL_BODY}
        showModal={showBalanceOrLimitError}
        buttonText={DISCARD_REFUND_BUTTON}
        onClickClose={handleBalanceErrorModalCloseBtn}
        onClickButton={handleBalanceErrorModalDiscardBtn}
      />
      <TransactionTimeoutErrorModal
        title={TIMEOUT_ERROR_MODAL_TITLE}
        body={REFUND_TIMEOUT_ERROR_MODAL_BODY}
        showModal={showTransactionTimeoutError}
        onClickClose={handleTimeoutErrorModalDismiss}
        onClickButton={handleTimeoutErrorModalDismiss}
        doubleOptions
        classNames="h-310"
        leftButtonText={DISCARD_REFUND_BUTTON}
        rightButtonText={TRY_AGAIN_BUTTON}
        onLeftButtonClick={handleTimeoutDiscardRefundModal}
        onRightButtonClick={handleTimeoutErrorModalDismiss}
      />
      <div className="mb-3 flex-col items-start justify-between">
        <ButtonComponent
          label="Back"
          onClick={handleBackButton}
          id="back-button"
          size="large"
          type="tertiary"
          iconStart={<ChevronLeft />}
        />
        <PaymentsPinPageComponent
          hasError={formErrors.pin.hasError}
          id="send-money-transaction"
          title={TRANSACTION_PIN_PAGE_TITLE}
          subtitle={TRANSACTION_PIN_PAGE_SUBTITLE}
          labelText={TRANSACTION_PIN_PAGE_LABEL}
          validationMessage={formErrors.pin.message}
          pinLength={TRANSACTIONS_PIN_LENGTH}
          newPin={newPin}
          isContinueButtonDisabled={isContinueButtonDisabled()}
          onBlur={onPinBlur}
          onContinueButtonClick={handleSubmit}
          onPinChanged={onPinChanged}
          isLoading={loading}
        />
      </div>
    </>
  );
});

export default RefundTransactionPinPage;
